import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/shared/forms';
import { formatDateTime } from '@/shared/lib/datetime';
import { useDebounced } from '@/shared/lib/use-debounced';
import { Button, TextField } from '@/shared/ui';

import {
  useInvite,
  useResendInvitation,
  useRevokeInvitation,
  usePeopleSearch,
  useScopeInvitations,
} from './board-api';
import { grantedRoleLabel, toRoleInput, type WorkspaceRoleChoice } from './roles';
import type { CustomRole, MemberRole } from './types';
import styles from './Collab.module.css';
import { RoleSelect, WorkspaceRoleSelect } from './RoleSelect';

type Scope =
  | { kind: 'workspace'; id: string }
  | { kind: 'board'; id: string }
  | { kind: 'database'; id: string };

/**
 * Invite an existing account by its exact login or registered e-mail. Invitations are
 * delivered inside the application (no e-mail is sent) and grant nothing until accepted.
 */
export function InvitePanel({
  scope,
  allowedRoles,
  customRoles,
  canInvite = true,
}: {
  scope: Scope;
  allowedRoles: MemberRole[];
  /** Workspace invitations only: the custom roles the inviter may hand out. A board takes a
   * standard board role, so the board scope leaves this out. */
  customRoles?: readonly CustomRole[];
  /** False: only the list (and cancelling) - the viewer may not hand out any role. */
  canInvite?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const invitations = useScopeInvitations(scope);
  const invite = useInvite(scope);
  const revoke = useRevokeInvitation();
  const resend = useResendInvitation();
  const database = scope.kind === 'database';
  const [identifier, setIdentifier] = useState('');
  const [choice, setChoice] = useState<WorkspaceRoleChoice>({
    role: allowedRoles.at(-1) ?? 'member',
    customRoleId: null,
  });
  const [sent, setSent] = useState<string | null>(null);
  const people = usePeopleSearch(useDebounced(identifier, 250));
  // An exact login or an e-mail is typed as is; suggestions only help to find a colleague by name.
  const suggestions = identifier.includes('@')
    ? []
    : (people.data ?? []).filter(
        (person) => person.login.toLowerCase() !== identifier.trim().toLowerCase(),
      );

  return (
    <section className={styles.stack} aria-label={t('collab.invite.title')}>
      {canInvite ? (
        <form
          className={styles.inline}
          onSubmit={(event) => {
            event.preventDefault();
            const value = identifier.trim();
            if (!value) return;
            invite.mutate(
              { identifier: value, ...toRoleInput(choice) },
              {
                onSuccess: (created) => {
                  setSent(created.invitee_display_name);
                  setIdentifier('');
                },
              },
            );
          }}
        >
          <TextField
            label={t('collab.invite.identifier')}
            value={identifier}
            autoComplete="off"
            spellCheck={false}
            maxLength={254}
            onChange={(event) => {
              setIdentifier(event.target.value);
              setSent(null);
            }}
          />
          <label>
            <span>{t('collab.invite.role')}</span>
            {customRoles === undefined ? (
              <RoleSelect
                label={t('collab.invite.role')}
                value={choice.role ?? 'member'}
                roles={allowedRoles}
                size="md"
                onChange={(role) => {
                  setChoice({ role, customRoleId: null });
                }}
              />
            ) : (
              <WorkspaceRoleSelect
                label={t('collab.invite.role')}
                value={choice}
                standardRoles={allowedRoles}
                customRoles={customRoles}
                onChange={setChoice}
              />
            )}
          </label>
          <Button type="submit" variant="primary" disabled={invite.isPending || !identifier.trim()}>
            {t('collab.invite.send')}
          </Button>
          {suggestions.length > 0 && (
            <ul className={styles.suggestions} aria-label={t('collab.invite.suggestions')}>
              {suggestions.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    className={styles.suggestion}
                    onClick={() => {
                      setIdentifier(person.login);
                      setSent(null);
                    }}
                  >
                    <span>{person.display_name}</span>
                    <span className={styles.rowMeta}>@{person.login}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </form>
      ) : (
        <p className={styles.muted}>{t('collab.invite.noRoles')}</p>
      )}
      <p className={styles.muted}>
        {database ? t('collab.invite.hintDatabase') : t('collab.invite.hint')}
      </p>
      <FormError error={invite.error ?? resend.error ?? revoke.error} />
      {sent && (
        <p role="status" className={styles.muted}>
          {t('collab.invite.sent', { name: sent })}
        </p>
      )}
      <h4>{t('collab.invite.pending')}</h4>
      {(invitations.data ?? []).length === 0 ? (
        <p className={styles.muted}>{t('collab.invite.none')}</p>
      ) : (
        <ul className={styles.list}>
          {(invitations.data ?? []).map((item) => (
            <li key={item.id} className={styles.row} data-testid="pending-invitation">
              <span className={styles.rowMain}>
                <span>{item.invitee_display_name}</span>
                <span className={styles.rowMeta}>
                  {grantedRoleLabel(t, item.role, item.custom_role_name)} ·{' '}
                  {database &&
                    `${t('collab.invite.by', {
                      who: item.inviter_display_name ?? '—',
                      when: formatDateTime(item.created_at, i18n.language),
                    })} · `}
                  {t('collab.invite.expires', {
                    when: formatDateTime(item.expires_at, i18n.language),
                  })}
                </span>
                {database && (
                  <span className={styles.rowMeta}>
                    <strong>{t('collab.invite.status.pending')}</strong>
                    {item.current_access?.role &&
                      ` · ${t('collab.invite.alreadyHas', {
                        role: t(`roles.${item.current_access.role}`),
                      })}`}
                  </span>
                )}
              </span>
              {database && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={resend.isPending}
                  onClick={() => {
                    resend.mutate(item.id);
                  }}
                >
                  {t('collab.invite.resend')}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={revoke.isPending}
                onClick={() => {
                  revoke.mutate(item.id);
                }}
              >
                {database ? t('collab.invite.cancel') : t('collab.invite.revoke')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
